/**
 * The position of the customization text on each shoe (left / right) of a
 * product. Returns the text props to the ABC dialog; nothing is saved here.
 */
import { useRef, useState, type SubmitEvent } from 'react';
import type { ProductLine } from '@/features/product-lines/api';
import { productLinePictureUrl } from '@/features/product-lines/product-line-form';
import {
  filterMatrix,
  getTextZoneStyle,
  hexToRgb,
} from '@/shared/lib/text-zone';
import { Dialog } from '@/shared/ui/Dialog';
import { usePicture } from '@/shared/ui/use-picture';
import type { Product } from './api';
import {
  toSpecialPositionValues,
  type SpecialPositionValues,
  type TextPropsValues,
  type View,
} from './abc-form';
import { productPictureUrl } from './logic';
import { PreviewPictureField, TextZone } from './preview';
import { imageTransform, previewText, useArrowNudge } from './preview-logic';
import { kebabCase } from '@/shared/lib/kebab-case';

export interface TextSpecialPositioningDialogProps {
  product: Product;
  productLine: ProductLine;
  textProps: Record<string, unknown>;
  imageProps: Record<View, { scale: number; offsetX: number; offsetY: number }>;
  currentColor: string;
  onClose: (result: SpecialPositionValues | null) => void;
}

const FIELDS: {
  key: keyof TextPropsValues;
  label: string;
  placeholder: string;
}[] = [
  { key: 'scale', label: 'Scale', placeholder: 'Scale' },
  { key: 'offsetY', label: 'Y', placeholder: 'Y' },
  { key: 'offsetX', label: 'X', placeholder: 'X' },
];
const SMALL_FIELDS: {
  key: keyof TextPropsValues;
  label: string;
  placeholder: string;
}[] = [
  { key: 'rotate', label: 'Rotation', placeholder: 'Degrees' },
  { key: 'curve', label: 'Curvature', placeholder: 'Pixels' },
  { key: 'letterSpace', label: 'Letter spacing', placeholder: 'Pixels' },
];

export function TextSpecialPositioningDialog({
  product,
  productLine,
  textProps,
  imageProps,
  currentColor,
  onClose,
}: Readonly<TextSpecialPositioningDialogProps>) {
  const [values, setValues] = useState<SpecialPositionValues>(() =>
    toSpecialPositionValues(textProps),
  );
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Chosen pictures are previewed but never uploaded here (see migration.md).
  const pictures = { custom: usePicture(), custom2: usePicture() };
  const rows = {
    custom: useRef<HTMLDivElement>(null),
    custom2: useRef<HTMLDivElement>(null),
  };

  const set = (
    view: 'custom' | 'custom2',
    key: keyof TextPropsValues,
    value: number,
  ) =>
    setValues((prev) => ({ ...prev, [view]: { ...prev[view], [key]: value } }));

  useArrowNudge(rows, (view, dx, dy) =>
    setValues((prev) => ({
      ...prev,
      [view]: {
        ...prev[view],
        offsetX: prev[view].offsetX + dx,
        offsetY: prev[view].offsetY + dy,
      },
    })),
  );

  const scaleError = (view: 'custom' | 'custom2') => {
    const scale = values[view].scale;
    return !Number.isNaN(scale) && (scale < 0 || scale > 1000)
      ? 'Wrong scale'
      : null;
  };

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setErrorMessage(null);
    if (scaleError('custom') || scaleError('custom2')) {
      setErrorMessage('Please fix the errors');
      return;
    }
    onClose(values);
  };

  const textureImg = productLinePictureUrl(productLine.id, 'texture') ?? '';
  const keyboard = productLine.keyboard ?? {};
  // The weight is read on `keyboard` rather than `keyboard.latin`: never set, kept as-is (see migration.md).
  const weight = (keyboard as { fontWeight?: string }).fontWeight;
  const weightSuffix = weight === 'Regular' || !weight ? '' : `-${weight}`;
  const font = `${keyboard.latin?.font || 'DINCondensed'}${weightSuffix}`;
  const text = previewText(keyboard.latin?.maxChars);

  const fieldTestId = (
    view: 'custom' | 'custom2',
    key: keyof TextPropsValues,
  ) => `product-text-positioning-form-${view}-${kebabCase(key)}`;

  const column = (view: 'custom' | 'custom2', title: string) => (
    <div className="col-span-6">
      <h4>{title}</h4>
      {FIELDS.map(({ key, label, placeholder }) => (
        <div className="mb-2" key={key}>
          <label className="field-label" htmlFor={`tsp-${view}-${key}`}>
            {label}
          </label>
          <input
            id={`tsp-${view}-${key}`}
            data-testid={fieldTestId(view, key)}
            className="field"
            type="number"
            name={`${view}-${key}`}
            placeholder={placeholder}
            value={Number.isNaN(values[view][key]) ? '' : values[view][key]}
            onChange={(event) => set(view, key, event.target.valueAsNumber)}
          />
          <small
            hidden={!(submitted && key === 'scale' && scaleError(view))}
            data-testid={`${fieldTestId(view, key)}-error`}
          >
            Wrong scale
          </small>
        </div>
      ))}
      <div className="bs-row mb-2">
        {SMALL_FIELDS.map(({ key, label, placeholder }) => (
          <div className="col-span-4" key={key}>
            <label className="field-label" htmlFor={`tsp-${view}-${key}`}>
              {label}
            </label>
            <input
              id={`tsp-${view}-${key}`}
              data-testid={fieldTestId(view, key)}
              className="field"
              type="number"
              name={`${view}-${key}`}
              placeholder={placeholder}
              value={Number.isNaN(values[view][key]) ? '' : values[view][key]}
              onChange={(event) => set(view, key, event.target.valueAsNumber)}
            />
          </div>
        ))}
      </div>
      <div className="mb-2" ref={rows[view]} id={`special-${view}-picture-row`}>
        <PreviewPictureField
          kind="custom"
          id={`tsp-${view}-picture`}
          name={`${view}Picture`}
          label="Picture"
          picture={pictures[view]}
          serverUrl={productPictureUrl(product.id, view)}
          imageStyle={imageTransform(imageProps[view])}
          testId={`product-text-positioning-form-${view}-picture`}
          overlay={
            <TextZone
              style={{
                ...getTextZoneStyle({
                  textProps: values[view],
                  color: currentColor,
                  shadowType: productLine.shadowType,
                  font,
                }),
                backgroundImage: `url(${textureImg})`,
              }}
              text={text}
              textureImg={textureImg}
              curve={values[view].curve}
            />
          }
        />
      </div>
    </div>
  );

  return (
    <Dialog
      title="Text - Special Positioning"
      onClose={() => onClose(null)}
      testId="product-text-positioning-dialog"
    >
      <svg style={{ display: 'none' }} aria-hidden="true">
        <filter id="textZoneFilter">
          <feColorMatrix
            type="matrix"
            values={filterMatrix(hexToRgb(currentColor) ?? undefined)}
          />
        </filter>
      </svg>
      <form className="form popin-form" onSubmit={onSubmit} noValidate>
        <div className="bs-row mb-1">
          {column('custom', 'Text positioning / left shoe')}
          {column('custom2', 'Text positioning / right shoe')}
        </div>
        {errorMessage && (
          <div
            className="alert mb-0 pb-0"
            role="alert"
            data-testid="product-text-positioning-form-error"
          >
            <span className="error-msg">{errorMessage}</span>
          </div>
        )}
        <div className="mt-2 flex justify-center">
          <button
            className="button button-form"
            type="submit"
            data-testid="product-text-positioning-form-save-button"
          >
            Save
          </button>
        </div>
      </form>
    </Dialog>
  );
}
